import type { ContentPage } from './content-pages';
import { CONTENT_ARTICLE_ENTRIES } from './content-article-registry.generated';
import { GENERATED_CONTENT_UI } from './localized-content-ui.generated';
import type { LocalizedContentLanguage } from './content-route-manifest.generated';

export interface LocalizedContentEntry {
  language: LocalizedContentLanguage;
  page: ContentPage;
}

export const LOCALIZED_CONTENT_ENTRIES: LocalizedContentEntry[] = CONTENT_ARTICLE_ENTRIES.filter(
  ({ language }) => language !== 'en',
).map(({ language, page }) => ({
  language: language as LocalizedContentLanguage,
  page,
}));

export function localizedContentMap(language: string): Map<string, ContentPage> {
  return new Map(
    LOCALIZED_CONTENT_ENTRIES.filter((entry) => entry.language === language).map((entry) => [
      entry.page.slug,
      entry.page,
    ]),
  );
}

export type ContentUiKey =
  | 'home'
  | 'guides'
  | 'reviewed'
  | 'evidence'
  | 'onThisPage'
  | 'faq'
  | 'commonQuestions'
  | 'references'
  | 'sourcesTitle'
  | 'importantContext'
  | 'disclaimer'
  | 'continueLearning'
  | 'relatedGuides'
  | 'readGuide'
  | 'knowledgeCenter'
  | 'indexTitle'
  | 'indexLead'
  | 'explore'
  | 'ready'
  | 'ctaTitle'
  | 'ctaText'
  | 'startTest'
  | 'browseGuides'
  | 'searchLabel'
  | 'searchPlaceholder'
  | 'filterByTopic'
  | 'allTopics'
  | 'results'
  | 'clearFilters'
  | 'showMore'
  | 'noResults'
  | 'noResultsText';

const CURATED_CONTENT_UI: Record<string, Partial<Record<ContentUiKey, string>>> = {
  en: {
    home: 'Home',
    guides: 'IQ guides',
    reviewed: 'Reviewed',
    evidence: 'Evidence and limitations included',
    onThisPage: 'On this page',
    faq: 'Frequently asked questions',
    commonQuestions: 'Common questions',
    references: 'References',
    sourcesTitle: 'Sources and further reading',
    importantContext: 'Important context',
    disclaimer:
      'IQLabTest provides educational content and an indicative online assessment, not a clinical diagnosis or official standardized IQ credential. Use a qualified professional when a decision depends on a result.',
    continueLearning: 'Continue learning',
    relatedGuides: 'Related IQ guides',
    readGuide: 'Read guide',
    knowledgeCenter: 'IQ knowledge center',
    indexTitle: 'Understand cognition, without the hype',
    indexLead: 'Evidence-aware guides with uncertainty and limitations made visible.',
    explore: 'Explore',
    ready: 'Ready to begin?',
    ctaTitle: 'Explore your cognitive strengths',
    ctaText: 'Take IQLabTest’s structured 40-question assessment in your preferred language.',
    startTest: 'Start the IQ test',
    browseGuides: 'Browse IQ guides',
    searchLabel: 'Search the Knowledge Center',
    searchPlaceholder: 'Search scores, testing, memory, reasoning…',
    filterByTopic: 'Filter by topic',
    allTopics: 'All topics',
    results: 'guides found',
    clearFilters: 'Clear filters',
    showMore: 'Show more guides',
    noResults: 'No guides found',
    noResultsText: 'Try a broader search or choose another topic.',
  },
  es: {
    home: 'Inicio',
    guides: 'Guías de IQ',
    reviewed: 'Revisado',
    evidence: 'Incluye evidencia y limitaciones',
    onThisPage: 'En esta página',
    faq: 'Preguntas frecuentes',
    commonQuestions: 'Preguntas comunes',
    references: 'Referencias',
    sourcesTitle: 'Fuentes y lecturas adicionales',
    importantContext: 'Contexto importante',
    disclaimer:
      'IQLabTest ofrece contenido educativo y una evaluación en línea orientativa, no un diagnóstico clínico ni una credencial oficial. Consulta a un profesional cualificado cuando una decisión dependa del resultado.',
    continueLearning: 'Continúa aprendiendo',
    relatedGuides: 'Guías relacionadas',
    readGuide: 'Leer guía',
    knowledgeCenter: 'Centro de conocimiento sobre IQ',
    indexTitle: 'Comprende la cognición sin exageraciones',
    indexLead: 'Guías basadas en evidencia que muestran la incertidumbre y las limitaciones.',
    explore: 'Explorar',
    ready: '¿Listo para empezar?',
    ctaTitle: 'Explora tus fortalezas cognitivas',
    ctaText:
      'Realiza la evaluación estructurada de 40 preguntas de IQLabTest en tu idioma preferido.',
    startTest: 'Comenzar el test de IQ',
  },
  ja: {
    home: 'ãƒ›ãƒ¼ãƒ ',
    guides: 'IQã‚¬ã‚¤ãƒ‰',
    reviewed: 'æœ€çµ‚ç¢ºèª',
    evidence: 'æ ¹æ‹ ã¨é™ç•Œã‚’è¨˜è¼‰',
    onThisPage: 'ã“ã®ãƒšãƒ¼ã‚¸ã®å†…å®¹',
    faq: 'ã‚ˆãã‚ã‚‹è³ªå•',
    commonQuestions: 'ã‚ˆãã‚ã‚‹è³ªå•',
    references: 'å‚è€ƒè³‡æ–™',
    sourcesTitle: 'å‡ºå…¸ã¨å‚è€ƒæƒ…å ±',
    importantContext: 'é‡è¦ãªæ³¨æ„',
    disclaimer:
      'IQLabTestã¯æ•™è‚²æƒ…å ±ã¨å‚è€ƒç”¨ã‚ªãƒ³ãƒ©ã‚¤ãƒ³è©•ä¾¡ã‚’æä¾›ã—ã¾ã™ã€‚è‡¨åºŠè¨ºæ–­ã‚„å…¬å¼ã®æ¨™æº–åŒ–IQè³‡æ ¼ã§ã¯ã‚ã‚Šã¾ã›ã‚“ã€‚çµæžœã«åŸºã¥ãé‡è¦ãªåˆ¤æ–­ã«ã¯è³‡æ ¼ã‚’æŒã¤å°‚é–€å®¶ã¸ç›¸è«‡ã—ã¦ãã ã•ã„ã€‚',
    continueLearning: 'ã•ã‚‰ã«å­¦ã¶',
    relatedGuides: 'é–¢é€£ã™ã‚‹IQã‚¬ã‚¤ãƒ‰',
    readGuide: 'ã‚¬ã‚¤ãƒ‰ã‚’èª­ã‚€',
    knowledgeCenter: 'IQãƒŠãƒ¬ãƒƒã‚¸ã‚»ãƒ³ã‚¿ãƒ¼',
    indexTitle: 'èª‡å¼µãªã—ã§èªçŸ¥ã‚’ç†è§£ã™ã‚‹',
    indexLead: 'ä¸ç¢ºå®Ÿæ€§ã¨é™ç•Œã‚’æ˜Žç¢ºã«ã—ãŸã€æ ¹æ‹ ã‚’é‡è¦–ã™ã‚‹ã‚¬ã‚¤ãƒ‰ã§ã™ã€‚',
    explore: 'æŽ¢ã™',
    ready: 'å§‹ã‚ã‚‹æº–å‚™ã¯ã§ãã¾ã—ãŸã‹ï¼Ÿ',
    ctaTitle: 'èªçŸ¥ã®å¼·ã¿ã‚’æŽ¢ã‚‹',
    ctaText: 'å¸Œæœ›ã™ã‚‹è¨€èªžã§IQLabTestã®40å•è©•ä¾¡ã‚’å—ã‘ã¦ãã ã•ã„ã€‚',
    startTest: 'IQãƒ†ã‚¹ãƒˆã‚’é–‹å§‹',
  },
  ru: {
    home: 'Ð“Ð»Ð°Ð²Ð½Ð°Ñ',
    guides: 'ÐœÐ°Ñ‚ÐµÑ€Ð¸Ð°Ð»Ñ‹ Ð¾Ð± IQ',
    reviewed: 'ÐŸÑ€Ð¾Ð²ÐµÑ€ÐµÐ½Ð¾',
    evidence: 'Ð£ÐºÐ°Ð·Ð°Ð½Ñ‹ Ð´Ð°Ð½Ð½Ñ‹Ðµ Ð¸ Ð¾Ð³Ñ€Ð°Ð½Ð¸Ñ‡ÐµÐ½Ð¸Ñ',
    onThisPage: 'ÐÐ° ÑÑ‚Ð¾Ð¹ ÑÑ‚Ñ€Ð°Ð½Ð¸Ñ†Ðµ',
    faq: 'Ð§Ð°ÑÑ‚Ñ‹Ðµ Ð²Ð¾Ð¿Ñ€Ð¾ÑÑ‹',
    commonQuestions: 'Ð§Ð°ÑÑ‚Ñ‹Ðµ Ð²Ð¾Ð¿Ñ€Ð¾ÑÑ‹',
    references: 'Ð˜ÑÑ‚Ð¾Ñ‡Ð½Ð¸ÐºÐ¸',
    sourcesTitle: 'Ð˜ÑÑ‚Ð¾Ñ‡Ð½Ð¸ÐºÐ¸ Ð¸ Ð´Ð¾Ð¿Ð¾Ð»Ð½Ð¸Ñ‚ÐµÐ»ÑŒÐ½Ð¾Ðµ Ñ‡Ñ‚ÐµÐ½Ð¸Ðµ',
    importantContext: 'Ð’Ð°Ð¶Ð½Ñ‹Ð¹ ÐºÐ¾Ð½Ñ‚ÐµÐºÑÑ‚',
    disclaimer:
      'IQLabTest Ð¿Ñ€ÐµÐ´Ð¾ÑÑ‚Ð°Ð²Ð»ÑÐµÑ‚ Ð¾Ð±Ñ€Ð°Ð·Ð¾Ð²Ð°Ñ‚ÐµÐ»ÑŒÐ½Ñ‹Ðµ Ð¼Ð°Ñ‚ÐµÑ€Ð¸Ð°Ð»Ñ‹ Ð¸ Ð¾Ñ€Ð¸ÐµÐ½Ñ‚Ð¸Ñ€Ð¾Ð²Ð¾Ñ‡Ð½ÑƒÑŽ Ð¾Ð½Ð»Ð°Ð¹Ð½-Ð¾Ñ†ÐµÐ½ÐºÑƒ, Ð° Ð½Ðµ ÐºÐ»Ð¸Ð½Ð¸Ñ‡ÐµÑÐºÐ¸Ð¹ Ð´Ð¸Ð°Ð³Ð½Ð¾Ð· Ð¸Ð»Ð¸ Ð¾Ñ„Ð¸Ñ†Ð¸Ð°Ð»ÑŒÐ½Ñ‹Ð¹ Ð´Ð¾ÐºÑƒÐ¼ÐµÐ½Ñ‚. Ð”Ð»Ñ Ñ€ÐµÑˆÐµÐ½Ð¸Ð¹, Ð·Ð°Ð²Ð¸ÑÑÑ‰Ð¸Ñ… Ð¾Ñ‚ Ñ€ÐµÐ·ÑƒÐ»ÑŒÑ‚Ð°Ñ‚Ð°, Ð¾Ð±Ñ€Ð°Ñ‚Ð¸Ñ‚ÐµÑÑŒ Ðº ÐºÐ²Ð°Ð»Ð¸Ñ„Ð¸Ñ†Ð¸Ñ€Ð¾Ð²Ð°Ð½Ð½Ð¾Ð¼Ñƒ ÑÐ¿ÐµÑ†Ð¸Ð°Ð»Ð¸ÑÑ‚Ñƒ.',
    continueLearning: 'ÐŸÑ€Ð¾Ð´Ð¾Ð»Ð¶Ð¸Ñ‚ÑŒ Ð¸Ð·ÑƒÑ‡ÐµÐ½Ð¸Ðµ',
    relatedGuides: 'Ð¡Ð²ÑÐ·Ð°Ð½Ð½Ñ‹Ðµ Ð¼Ð°Ñ‚ÐµÑ€Ð¸Ð°Ð»Ñ‹',
    readGuide: 'Ð§Ð¸Ñ‚Ð°Ñ‚ÑŒ',
    knowledgeCenter: 'Ð¦ÐµÐ½Ñ‚Ñ€ Ð·Ð½Ð°Ð½Ð¸Ð¹ Ð¾Ð± IQ',
    indexTitle:
      'ÐŸÐ¾Ð½Ð¸Ð¼Ð°Ð¹Ñ‚Ðµ ÐºÐ¾Ð³Ð½Ð¸Ñ‚Ð¸Ð²Ð½Ñ‹Ðµ ÑÐ¿Ð¾ÑÐ¾Ð±Ð½Ð¾ÑÑ‚Ð¸ Ð±ÐµÐ· Ð³Ñ€Ð¾Ð¼ÐºÐ¸Ñ… Ð¾Ð±ÐµÑ‰Ð°Ð½Ð¸Ð¹',
    indexLead:
      'ÐœÐ°Ñ‚ÐµÑ€Ð¸Ð°Ð»Ñ‹ Ñ Ð¾Ð¿Ð¾Ñ€Ð¾Ð¹ Ð½Ð° Ð´Ð°Ð½Ð½Ñ‹Ðµ, Ð³Ð´Ðµ Ð²Ð¸Ð´Ð½Ñ‹ Ð½ÐµÐ¾Ð¿Ñ€ÐµÐ´ÐµÐ»Ñ‘Ð½Ð½Ð¾ÑÑ‚ÑŒ Ð¸ Ð¾Ð³Ñ€Ð°Ð½Ð¸Ñ‡ÐµÐ½Ð¸Ñ.',
    explore: 'Ð¡Ð¼Ð¾Ñ‚Ñ€ÐµÑ‚ÑŒ',
    ready: 'Ð“Ð¾Ñ‚Ð¾Ð²Ñ‹ Ð½Ð°Ñ‡Ð°Ñ‚ÑŒ?',
    ctaTitle: 'Ð˜Ð·ÑƒÑ‡Ð¸Ñ‚Ðµ ÑÐ²Ð¾Ð¸ ÐºÐ¾Ð³Ð½Ð¸Ñ‚Ð¸Ð²Ð½Ñ‹Ðµ ÑÐ¸Ð»ÑŒÐ½Ñ‹Ðµ ÑÑ‚Ð¾Ñ€Ð¾Ð½Ñ‹',
    ctaText:
      'ÐŸÑ€Ð¾Ð¹Ð´Ð¸Ñ‚Ðµ ÑÑ‚Ñ€ÑƒÐºÑ‚ÑƒÑ€Ð¸Ñ€Ð¾Ð²Ð°Ð½Ð½Ñ‹Ð¹ Ñ‚ÐµÑÑ‚ IQLabTest Ð¸Ð· 40 Ð²Ð¾Ð¿Ñ€Ð¾ÑÐ¾Ð² Ð½Ð° ÑƒÐ´Ð¾Ð±Ð½Ð¾Ð¼ ÑÐ·Ñ‹ÐºÐµ.',
    startTest: 'ÐÐ°Ñ‡Ð°Ñ‚ÑŒ Ñ‚ÐµÑÑ‚ IQ',
  },
  ar: {
    home: 'Ø§Ù„Ø±Ø¦ÙŠØ³ÙŠØ©',
    guides: 'Ø£Ø¯Ù„Ø© Ø§Ù„Ø°ÙƒØ§Ø¡',
    reviewed: 'ØªÙ…Øª Ø§Ù„Ù…Ø±Ø§Ø¬Ø¹Ø©',
    evidence: 'ÙŠØªØ¶Ù…Ù† Ø§Ù„Ø£Ø¯Ù„Ø© ÙˆØ§Ù„Ù‚ÙŠÙˆØ¯',
    onThisPage: 'ÙÙŠ Ù‡Ø°Ù‡ Ø§Ù„ØµÙØ­Ø©',
    faq: 'Ø§Ù„Ø£Ø³Ø¦Ù„Ø© Ø§Ù„Ø´Ø§Ø¦Ø¹Ø©',
    commonQuestions: 'Ø£Ø³Ø¦Ù„Ø© Ø´Ø§Ø¦Ø¹Ø©',
    references: 'Ø§Ù„Ù…Ø±Ø§Ø¬Ø¹',
    sourcesTitle: 'Ø§Ù„Ù…ØµØ§Ø¯Ø± ÙˆÙ‚Ø±Ø§Ø¡Ø§Øª Ø¥Ø¶Ø§ÙÙŠØ©',
    importantContext: 'Ø³ÙŠØ§Ù‚ Ù…Ù‡Ù…',
    disclaimer:
      'ÙŠÙ‚Ø¯Ù… IQLabTest Ù…Ø­ØªÙˆÙ‰ ØªØ¹Ù„ÙŠÙ…ÙŠØ§Ù‹ ÙˆØªÙ‚ÙŠÙŠÙ…Ø§Ù‹ Ø¥Ø±Ø´Ø§Ø¯ÙŠØ§Ù‹ Ø¹Ø¨Ø± Ø§Ù„Ø¥Ù†ØªØ±Ù†ØªØŒ ÙˆÙ„ÙŠØ³ ØªØ´Ø®ÙŠØµØ§Ù‹ Ø³Ø±ÙŠØ±ÙŠØ§Ù‹ Ø£Ùˆ Ø§Ø¹ØªÙ…Ø§Ø¯Ø§Ù‹ Ø±Ø³Ù…ÙŠØ§Ù‹. Ø§Ø³ØªØ´Ø± Ù…Ø®ØªØµØ§Ù‹ Ù…Ø¤Ù‡Ù„Ø§Ù‹ Ø¹Ù†Ø¯Ù…Ø§ ÙŠØ¹ØªÙ…Ø¯ Ù‚Ø±Ø§Ø± Ø¹Ù„Ù‰ Ø§Ù„Ù†ØªÙŠØ¬Ø©.',
    continueLearning: 'ØªØ§Ø¨Ø¹ Ø§Ù„ØªØ¹Ù„Ù‘Ù…',
    relatedGuides: 'Ø£Ø¯Ù„Ø© Ø°Ø§Øª ØµÙ„Ø©',
    readGuide: 'Ø§Ù‚Ø±Ø£ Ø§Ù„Ø¯Ù„ÙŠÙ„',
    knowledgeCenter: 'Ù…Ø±ÙƒØ² Ù…Ø¹Ø±ÙØ© Ø§Ù„Ø°ÙƒØ§Ø¡',
    indexTitle: 'Ø§ÙÙ‡Ù… Ø§Ù„Ù‚Ø¯Ø±Ø§Øª Ø§Ù„Ù…Ø¹Ø±ÙÙŠØ© Ø¨Ù„Ø§ Ù…Ø¨Ø§Ù„ØºØ©',
    indexLead: 'Ø£Ø¯Ù„Ø© ØªØ±Ø§Ø¹ÙŠ Ø§Ù„Ø£Ø¯Ù„Ø© ÙˆØªÙˆØ¶Ø­ Ø¹Ø¯Ù… Ø§Ù„ÙŠÙ‚ÙŠÙ† ÙˆØ§Ù„Ù‚ÙŠÙˆØ¯.',
    explore: 'Ø§Ø³ØªÙƒØ´Ù',
    ready: 'Ù‡Ù„ Ø£Ù†Øª Ù…Ø³ØªØ¹Ø¯ØŸ',
    ctaTitle: 'Ø§Ø³ØªÙƒØ´Ù Ù…ÙˆØ§Ø·Ù† Ù‚ÙˆØªÙƒ Ø§Ù„Ù…Ø¹Ø±ÙÙŠØ©',
    ctaText:
      'Ø£Ø¬Ø±Ù ØªÙ‚ÙŠÙŠÙ… IQLabTest Ø§Ù„Ù…Ù†Ø¸Ù… Ø§Ù„Ù…Ø¤Ù„Ù Ù…Ù† 40 Ø³Ø¤Ø§Ù„Ø§Ù‹ Ø¨Ù„ØºØªÙƒ Ø§Ù„Ù…ÙØ¶Ù„Ø©.',
    startTest: 'Ø§Ø¨Ø¯Ø£ Ø§Ø®ØªØ¨Ø§Ø± Ø§Ù„Ø°ÙƒØ§Ø¡',
  },
  ko: {
    home: 'í™ˆ',
    guides: 'IQ ê°€ì´ë“œ',
    reviewed: 'ê²€í† ì¼',
    evidence: 'ê·¼ê±°ì™€ í•œê³„ í¬í•¨',
    onThisPage: 'ì´ íŽ˜ì´ì§€ì—ì„œ',
    faq: 'ìžì£¼ ë¬»ëŠ” ì§ˆë¬¸',
    commonQuestions: 'ìžì£¼ ë¬»ëŠ” ì§ˆë¬¸',
    references: 'ì°¸ê³ ë¬¸í—Œ',
    sourcesTitle: 'ì¶œì²˜ì™€ ì¶”ê°€ ìžë£Œ',
    importantContext: 'ì¤‘ìš”í•œ ì•ˆë‚´',
    disclaimer:
      'IQLabTestëŠ” êµìœ¡ ì½˜í…ì¸ ì™€ ì°¸ê³ ìš© ì˜¨ë¼ì¸ í‰ê°€ë¥¼ ì œê³µí•˜ë©° ìž„ìƒ ì§„ë‹¨ì´ë‚˜ ê³µì‹ í‘œì¤€í™” IQ ìžê²©ì´ ì•„ë‹™ë‹ˆë‹¤. ê²°ê³¼ê°€ ì¤‘ìš”í•œ ê²°ì •ì— ì‚¬ìš©ë  ë•ŒëŠ” ìžê²©ì„ ê°–ì¶˜ ì „ë¬¸ê°€ì™€ ìƒë‹´í•˜ì„¸ìš”.',
    continueLearning: 'ê³„ì† ì•Œì•„ë³´ê¸°',
    relatedGuides: 'ê´€ë ¨ IQ ê°€ì´ë“œ',
    readGuide: 'ê°€ì´ë“œ ì½ê¸°',
    knowledgeCenter: 'IQ ì§€ì‹ ì„¼í„°',
    indexTitle: 'ê³¼ìž¥ ì—†ì´ ì¸ì§€ ëŠ¥ë ¥ ì´í•´í•˜ê¸°',
    indexLead: 'ë¶ˆí™•ì‹¤ì„±ê³¼ í•œê³„ë¥¼ ë¶„ëª…ížˆ ì œì‹œí•˜ëŠ” ê·¼ê±° ì¤‘ì‹¬ ê°€ì´ë“œìž…ë‹ˆë‹¤.',
    explore: 'ì‚´íŽ´ë³´ê¸°',
    ready: 'ì‹œìž‘í•  ì¤€ë¹„ê°€ ë˜ì…¨ë‚˜ìš”?',
    ctaTitle: 'ì¸ì§€ì  ê°•ì ì„ ì‚´íŽ´ë³´ì„¸ìš”',
    ctaText: 'ì›í•˜ëŠ” ì–¸ì–´ë¡œ IQLabTestì˜ 40ë¬¸í•­ í‰ê°€ë¥¼ ì§„í–‰í•˜ì„¸ìš”.',
    startTest: 'IQ í…ŒìŠ¤íŠ¸ ì‹œìž‘',
  },
  id: {
    home: 'Beranda',
    guides: 'Panduan IQ',
    reviewed: 'Ditinjau',
    evidence: 'Menyertakan bukti dan batasan',
    onThisPage: 'Di halaman ini',
    faq: 'Pertanyaan umum',
    commonQuestions: 'Pertanyaan umum',
    references: 'Referensi',
    sourcesTitle: 'Sumber dan bacaan lanjutan',
    importantContext: 'Konteks penting',
    disclaimer:
      'IQLabTest menyediakan konten pendidikan dan asesmen online indikatif, bukan diagnosis klinis atau kredensial IQ resmi. Gunakan tenaga profesional yang berkualifikasi bila keputusan bergantung pada hasil.',
    continueLearning: 'Lanjutkan belajar',
    relatedGuides: 'Panduan IQ terkait',
    readGuide: 'Baca panduan',
    knowledgeCenter: 'Pusat pengetahuan IQ',
    indexTitle: 'Pahami kognisi tanpa klaim berlebihan',
    indexLead: 'Panduan berbasis bukti yang menampilkan ketidakpastian dan batasan.',
    explore: 'Jelajahi',
    ready: 'Siap memulai?',
    ctaTitle: 'Jelajahi kekuatan kognitif Anda',
    ctaText: 'Ikuti asesmen IQLabTest terstruktur dengan 40 pertanyaan dalam bahasa pilihan Anda.',
    startTest: 'Mulai tes IQ',
  },
};

const CONTENT_UI_CATALOGS = {
  ...CURATED_CONTENT_UI,
  ...GENERATED_CONTENT_UI,
};

export const CONTENT_UI = Object.fromEntries(
  Object.entries(CONTENT_UI_CATALOGS).map(([language, catalog]) => [
    language,
    { ...CURATED_CONTENT_UI['en'], ...catalog },
  ]),
) as Record<string, Record<ContentUiKey, string>>;
